'use client';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import type { AriaAttributes, InputHTMLAttributes } from 'react';
import type { PhoneInputProps, CountryData } from 'react-international-phone';
import { PhoneInput, defaultCountries } from 'react-international-phone';
import './phone-input-styles.css';

export const PhoneInputField = forwardRef<
  HTMLInputElement,
  PhoneInputProps &
    AriaAttributes &
    Pick<InputHTMLAttributes<HTMLInputElement>, 'id' | 'autoComplete' | 'tabIndex' | 'title'> & {
      variant?: 'outlined' | 'default';
    }
>(function PhoneInputField(
  { className, variant, id, autoComplete, tabIndex, title, inputProps, ...props },
  ref,
) {
  const [focused, setFocused] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useCallback(
    (input: HTMLInputElement | null) => {
      if (typeof ref === 'function') return ref(input);
      if (ref) ref.current = input;
    },
    [ref],
  );
  const ariaProps: AriaAttributes = {};
  const phoneProps = { ...props };
  for (const key of Object.keys(phoneProps)) {
    if (key.startsWith('aria-')) {
      const ariaKey = key as keyof AriaAttributes;
      Object.assign(ariaProps, { [ariaKey]: phoneProps[ariaKey] });
      delete phoneProps[ariaKey];
    }
  }

  const customCountries = defaultCountries.map(country => {
    if (country[0] === 'Slovakia') {
      return ['Slovakia', 'sk', '421', '... ... ...'] as CountryData;
    }
    return country;
  });

  const locale = useLocaleStore(s => s.currentLocale);

  const defaultCountryFromLocale = () => {
    if (locale === 'cs') {
      return 'cz';
    }

    return locale;
  };

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      const current = wrapperRef.current;

      if (current && !current.contains(event.target)) {
        setFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      className={cn(
        'flex h-[52px] w-full items-center rounded-[20px] border border-border',
        className,
      )}
    >
      <PhoneInput
        ref={inputRef}
        defaultCountry={defaultCountryFromLocale()}
        name="phone"
        countries={customCountries}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        inputClassName={props.inputClassName}
        inputStyle={{
          height: '40px',
          paddingLeft: '12px',
          width: '100%',
          border: 'none',
          borderRadius: 0,
          backgroundColor: 'transparent',
          outline: 'none',
        }}
        countrySelectorStyleProps={{
          buttonClassName: '',
          buttonStyle: {
            height: '36px',
            border: 'none',
            borderRadius: '20px',
            backgroundColor: '#F3F4F6',
            margin: '6px',
            paddingLeft: '24px',
            paddingRight: '24px',
            gap: '6px',
          },
        }}
        {...phoneProps}
        inputProps={{ id, autoComplete, tabIndex, title, ...ariaProps, ...inputProps }}
      />
    </div>
  );
});

PhoneInputField.displayName = 'PhoneInputField';
